import { FunctionComponent, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "@emotion/styled";

import {
  Badge,
  Box,
  IconButton,
  ListItem,
  ListItemText,
  Popover as MuiPopover,
  Tooltip,
  List,
} from "@mui/material";
import { Bell } from "react-feather";
import { useTranslation } from "react-i18next";
import { notificationApi } from "core/api";

const Popover = styled(MuiPopover)`
  .MuiPaper-root {
    width: 320px;
    ${(props) => props.theme.shadows[1]};
    border: 1px solid ${(props) => props.theme.palette.divider};
  }
`;

const Indicator = styled(Badge)`
  .MuiBadge-badge {
    background: ${(props) => props.theme.header.indicator.background};
    color: ${(props) => props.theme.palette.common.white};
  }
`;

type Notif = {
  id: number;
  type: string;
  title: string;
  body?: string;
  link?: string;
  is_read: boolean;
  created_at: string;
};

interface Props {}

const NavbarNotificationsDropdown: FunctionComponent<Props> = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const ref = useRef(null);
  const [isOpen, setOpen] = useState(false);
  const [items, setItems] = useState<Notif[]>([]);
  const [unread, setUnread] = useState(0);

  const load = async () => {
    try {
      const list = await notificationApi.getList();
      setItems(Array.isArray(list) ? list : []);
      const c = await notificationApi.getUnreadCount();
      setUnread(c || 0);
    } catch (e) {
      // silenzioso: se l'endpoint non c'è o l'utente non è loggato
    }
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 30000);
    return () => clearInterval(iv);
  }, []);

  const handleOpen = async () => {
    setOpen(true);
    try {
      await notificationApi.markRead();
      setUnread(0);
      setItems((prev) => prev.map((n) => ({ ...n, is_read: true })));
    } catch (e) {
      /* noop */
    }
  };

  const handleClose = () => setOpen(false);

  const goTo = (n: Notif) => {
    handleClose();
    if (n.link) {
      if (n.link.startsWith("http")) {
        window.location.href = n.link;
      } else {
        navigate(n.link);
      }
    }
  };

  return (
    <>
      <Tooltip title={t("header.notifications", { defaultValue: "Notifiche" })}>
        <IconButton color="inherit" ref={ref} onClick={handleOpen} size="large">
          <Indicator badgeContent={unread}>
            <Bell color="#1D2C54" />
          </Indicator>
        </IconButton>
      </Tooltip>
      <Popover
        anchorEl={ref.current}
        open={isOpen}
        onClose={handleClose}
        anchorOrigin={{ horizontal: "center", vertical: "bottom" }}
        transformOrigin={{ horizontal: "center", vertical: "top" }}
      >
        <Box sx={{ p: 1.5, fontWeight: 700, borderBottom: "1px solid #eee" }}>
          {t("header.notifications", { defaultValue: "Notifiche" })}
        </Box>
        <List sx={{ maxHeight: 380, overflowY: "auto", p: 0 }}>
          {items.length === 0 && (
            <ListItem>
              <ListItemText
                primary={t("header.no_notifications", {
                  defaultValue: "Nessuna notifica",
                })}
                primaryTypographyProps={{ variant: "body2", color: "text.secondary" }}
              />
            </ListItem>
          )}
          {items.map((n) => (
            <ListItem
              key={n.id}
              divider
              onClick={() => goTo(n)}
              sx={{
                cursor: n.link ? "pointer" : "default",
                background: n.is_read ? "transparent" : "#fff7f0",
                "&:hover": { background: "#f5f6f8" },
              }}
            >
              <ListItemText
                primary={n.title}
                secondary={n.body}
                primaryTypographyProps={{
                  variant: "subtitle2",
                  fontWeight: n.is_read ? 400 : 700,
                  color: "textPrimary",
                }}
                secondaryTypographyProps={{ variant: "caption" }}
              />
            </ListItem>
          ))}
        </List>
      </Popover>
    </>
  );
};

export default NavbarNotificationsDropdown;
